import MenuView from "../../components/MenuView";
import ReactECharts from "./DashboardCharts/PieCharts";
import BarChart from "./DashboardCharts/BarCharts";
import LineChart from "./DashboardCharts/LineChart";
import ProductChart from "./DashboardCharts/ProductChart";
import OrderStatusDistribution from "./DashboardCharts/OrderStatusDistribution";
import {
  Backdrop,
  Card,
  CircularProgress,
  Grid,
  Typography,
  useMediaQuery,
  useTheme,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  Chip,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
} from "@mui/material";
import { useEffect } from "react";
import { useState } from "react";
import LoadApi from "../../hooks/useApi";
import useSnack from "../../hooks/useSnack";
import SnackAlert from "../../components/SnackAlert";
import MinimalistCard from "../../components/MinimalistCard";

export default function View() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [limit, setLimit] = useState(8); // Cambia el límite aquí
  const { loadApi } = LoadApi();
  const { showSnack, msgSB, openSB, typeSB } = useSnack();

  const getData = () => {
    setLoading(true);
    loadApi(`admin/dashboard/?limit=${limit}`, true, "get")
      .then((response) => {
        setData(response.data);
        console.log("graficas", response.data);
      })
      .catch((error) => {
        showSnack(error.message, "error");
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleLimitChange = (event) => {
    setLimit(event.target.value);
    getData(); // Recargar los datos con el nuevo límite
  };

  useEffect(() => {
    getData();
  }, [limit]);

  // Función para formatear moneda
  const formatCurrency = (value) => {
    return new Intl.NumberFormat("es-ES", {
      style: "currency",
      currency: "EUR",
      minimumFractionDigits: 2,
    }).format(value || 0);
  };

  // Obtener los productos más vendidos de los pedidos recientes
  const getTopSellingProducts = () => {
    const productCounts = {};

    (data?.summary?.recentOrders || []).forEach((order) => {
      order.OrderItems.forEach((item) => {
        if (productCounts[item.name]) {
          productCounts[item.name] += parseInt(item.quantity);
        } else {
          productCounts[item.name] = parseInt(item.quantity);
        }
      });
    });

    return Object.entries(productCounts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5)
      .map(([name, count]) => ({ name, count }));
  };

  // Obtener distribución de estados de pedidos
  const getOrderStatusDistribution = () => {
    const statusCounts = {};

    (data?.summary?.recentOrders || []).forEach((order) => {
      if (statusCounts[order.status]) {
        statusCounts[order.status] += 1;
      } else {
        statusCounts[order.status] = 1;
      }
    });

    return Object.entries(statusCounts).map(([status, count]) => ({
      status,
      count,
    }));
  };

  const getOrderOriginDistribution = () => {
    const originCounts = {};

    (data?.summary?.recentOrders || []).forEach((order) => {
      const origen = order.origen || "Desconocido";
      if (originCounts[origen]) {
        originCounts[origen] += 1;
      } else {
        originCounts[origen] = 1;
      }
    });

    return Object.entries(originCounts).map(([origen, count]) => ({
      origen,
      count,
    }));
  };

  const originDistribution = getOrderOriginDistribution();
  const statusDistribution = getOrderStatusDistribution();

  // Altura común para escritorio (basada en el contenido más grande, usualmente origen)
  const desktopHeight = `${Math.max(480, 320 + Math.ceil(originDistribution.length / 2) * 30)}px`;

  // Alturas independientes para móvil, sincronizadas para escritorio
  const statusChartHeight = isMobile
    ? `${350 + statusDistribution.length * 25}px`
    : desktopHeight;

  const originChartHeight = isMobile
    ? `${350 + originDistribution.length * 25}px`
    : desktopHeight;

  return (
    <>
      <MenuView>
        <Box
          sx={{
            height: { xs: "100vh", md: "96vh" },
            overflow: "auto",
            p: 1,
            "&::-webkit-scrollbar": {
              width: "0.4em",
            },
            "&::-webkit-scrollbar-track": {
              background: "transparent",
            },
            "&::-webkit-scrollbar-thumb": {
              background: (theme) => theme.palette.grey[300],
              borderRadius: "4px",
            },
            padding: 2,
          }}
        >
          <Typography variant="h4" align="left" mb={1}>
            Dashboard
          </Typography>
          <Grid container spacing={3}>
            {/* Stats Cards - Row 1 */}
            <Grid item xs={6} md={4}>
              <MinimalistCard sx={{ p: 1, textAlign: "center" }}>
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                >
                  <Box textAlign="center">
                    <Typography
                      variant="body2"
                      color="textSecondary"
                      sx={{ whiteSpace: "nowrap", fontWeight: 700 }}
                      fontSize={isMobile ? "0.95rem" : "1rem"}
                    >
                      Ventas Totales
                    </Typography>
                    <Typography
                      variant="h4"
                      sx={{ mt: 1, fontSize: isMobile ? "1rem" : "2rem" }}
                    >
                      {formatCurrency(data?.sales?.totalSales)}
                    </Typography>
                  </Box>
                </Box>
              </MinimalistCard>
            </Grid>

            <Grid item xs={6} md={4}>
              <MinimalistCard sx={{ p: 1, textAlign: "center" }}>
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                >
                  <Box textAlign="center">
                    <Typography
                      variant="body2"
                      color="textSecondary"
                      sx={{ fontWeight: 700 }}
                      fontSize={isMobile ? "0.95rem" : "1rem"}
                    >
                      Total Pedidos
                    </Typography>
                    <Typography
                      variant="h4"
                      sx={{ mt: 1, fontSize: isMobile ? "1rem" : "2rem" }}
                    >
                      {data?.sales?.totalOrders || 0}
                    </Typography>
                  </Box>
                </Box>
              </MinimalistCard>
            </Grid>

            <Grid item xs={6} md={4}>
              <MinimalistCard sx={{ p: 1, textAlign: "center" }}>
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                >
                  <Box textAlign="center">
                    <Typography
                      variant="body2"
                      color="textSecondary"
                      sx={{ whiteSpace: "nowrap", fontWeight: 700 }}
                      fontSize={isMobile ? "0.95rem" : "1rem"}
                    >
                      Total Usuarios
                    </Typography>
                    <Typography
                      variant="h4"
                      sx={{ mt: 1, fontSize: isMobile ? "1rem" : "2rem" }}
                    >
                      {data?.summary?.userCount || 0}
                    </Typography>
                  </Box>
                </Box>
              </MinimalistCard>
            </Grid>

            <Grid item xs={6} md={4}>
              <MinimalistCard sx={{ p: 1, textAlign: "center" }}>
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                >
                  <Box textAlign="center">
                    {isMobile ? (
                      <Typography
                        variant="body2"
                        color="textSecondary"
                        sx={{
                          whiteSpace: "nowrap",
                          fontSize: { xs: "0.8rem" },
                          fontWeight: 700,
                        }}
                        fontSize={isMobile ? "0.95rem" : "1rem"}
                      >
                        Productos Vend.
                      </Typography>
                    ) : (
                      <Typography
                        variant="body2"
                        color="textSecondary"
                        sx={{ fontWeight: 700 }}
                        fontSize={isMobile ? "0.95rem" : "1rem"}
                      >
                        Productos Vendidos
                      </Typography>
                    )}

                    <Typography
                      variant="h4"
                      sx={{ mt: 1, fontSize: isMobile ? "1rem" : "2rem" }}
                    >
                      {data?.sales?.totalProductsSold || 0}
                    </Typography>
                  </Box>
                </Box>
              </MinimalistCard>
            </Grid>

            {/* Stats Cards - Row 2 */}
            <Grid item xs={6} md={4}>
              <MinimalistCard sx={{ p: 1, textAlign: "center" }}>
                <Box display="flex" flexDirection="column" alignItems="center">
                  <Typography
                    variant="body2"
                    color="textSecondary"
                    sx={{ fontWeight: 700 }}
                    fontSize={isMobile ? "0.95rem" : "1rem"}
                  >
                    Ticket Promedio
                  </Typography>
                  <Typography
                    variant="h5"
                    sx={{ mt: 1, fontSize: isMobile ? "1rem" : "1.8rem" }}
                  >
                    {formatCurrency(data?.sales?.averageTicket)}
                  </Typography>
                </Box>
              </MinimalistCard>
            </Grid>

            <Grid item xs={6} md={4}>
              <MinimalistCard sx={{ p: 1, textAlign: "center" }}>
                <Box display="flex" flexDirection="column" alignItems="center">
                  {isMobile ? (
                    <Typography
                      variant="body2"
                      color="textSecondary"
                      sx={{ whiteSpace: "nowrap", fontSize: { xs: "0.8rem" }, fontWeight: 700 }}
                      fontSize={isMobile ? "0.95rem" : "1rem"}
                    >
                      Productos dispo.
                    </Typography>
                  ) : (
                    <Typography
                      variant="body2"
                      color="textSecondary"
                      sx={{ fontWeight: 700 }}
                      fontSize={isMobile ? "0.95rem" : "1rem"}
                    >
                      Productos Disponibles
                    </Typography>
                  )}
                  <Typography
                    variant="h5"
                    sx={{ mt: 1, fontSize: isMobile ? "1rem" : "1.8rem" }}
                  >
                    {data?.sales?.totalProducts || 0}
                  </Typography>
                </Box>
              </MinimalistCard>
            </Grid>

            {/* Charts - First Row */}
            <Grid item xs={12} md={6}>
              <MinimalistCard>
                <Typography variant="h6">Estado de Pedidos</Typography>
                <Box
                  style={{
                    height: statusChartHeight,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                  }}
                >
                  <ReactECharts
                    orders={data?.summary?.recentOrders || []}
                    style={{ height: "100%", width: "100%" }}
                    isMobile={isMobile}
                  />
                </Box>
              </MinimalistCard>
            </Grid>

            <Grid item xs={12} md={6}>
              <MinimalistCard>
                <Typography variant="h6">Origen de Pedidos</Typography>
                <Box
                  style={{
                    height: originChartHeight,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                  }}
                >
                  <OrderStatusDistribution
                    data={originDistribution}
                    isMobile={isMobile}
                  />
                </Box>
              </MinimalistCard>
            </Grid>

            {/* Charts - Second Row */}
            <Grid item xs={12} md={6}>
              <MinimalistCard>
                <Typography variant="h6">Productos Más Vendidos</Typography>
                <Box style={{ height: isMobile ? "300px" : "350px" }}>
                  <ProductChart products={getTopSellingProducts()} />
                </Box>
              </MinimalistCard>
            </Grid>

            <Grid item xs={12} md={6}>
              <MinimalistCard>
                <Typography variant="h6" mb={2}>
                  Comparativa de Ventas
                </Typography>
                <Box style={{ height: isMobile ? "300px" : "335px" }}>
                  <BarChart totalSales={data?.sales?.monthlySales || 0} />
                </Box>
              </MinimalistCard>
            </Grid>

            <Grid item xs={12} md={12}>
              <MinimalistCard>
                <Typography variant="h6" mb={3}>
                  Tendencia de Ventas
                </Typography>
                <Box style={{ height: isMobile ? "300px" : "300px" }}>
                  <LineChart orders={data?.summary?.recentOrders || []} />
                </Box>
              </MinimalistCard>
            </Grid>

            {/* Recent Orders Table */}
            <Grid item xs={12}>
              <MinimalistCard sx={{ p: 2 }}>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  mb={2}
                >
                  <Box display="flex" alignItems="center" gap={2}>
                    <Typography variant="h6">Últimos pedidos</Typography>
                    <FormControl size="small" sx={{ minWidth: 120 }}>
                      <InputLabel>Mostrar</InputLabel>
                      <Select
                        value={limit}
                        label="Mostrar"
                        onChange={handleLimitChange}
                      >
                        <MenuItem value={5}>5</MenuItem>
                        <MenuItem value={8}>8</MenuItem>
                        <MenuItem value={10}>10</MenuItem>
                        <MenuItem value={15}>15</MenuItem>
                        <MenuItem value={20}>20</MenuItem>
                      </Select>
                    </FormControl>
                  </Box>
              
                </Box>
                <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
                  <Table stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>N° Pedido</TableCell>
                        <TableCell>Cliente</TableCell>
                        <TableCell>Total</TableCell>
                        <TableCell>Estado</TableCell>
                        <TableCell>Origen</TableCell>
                        <TableCell>Fecha</TableCell>
                        <TableCell>Productos</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(data?.summary?.fiveOrders || []).map((order) => (
                        <TableRow key={order.id}>
                          <TableCell>{order.order_number}</TableCell>
                          <TableCell>
                            {order.BillingInfo.infoBilling.split("\n")[0]}
                          </TableCell>
                          <TableCell>
                            {formatCurrency(Number(order.total_cost))}
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={order.status}
                              color={
                                order.status === "Completado"
                                  ? "success"
                                  : order.status === "Cancelado"
                                  ? "error"
                                  : "warning"
                              }
                              size="small"
                            />
                          </TableCell>
                          <TableCell>{order.origen || "N/A"}</TableCell>
                          <TableCell>
                            {new Date(order.createdAt).toLocaleDateString(
                              "es-ES",
                              {
                                day: "2-digit",
                                month: "2-digit",
                                year: "numeric",
                              }
                            )}
                          </TableCell>
                          <TableCell>{order.OrderItems.length}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </MinimalistCard>
            </Grid>
          </Grid>
        </Box>
      </MenuView>

      <Backdrop
        sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={loading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <SnackAlert openSB={openSB} typeSB={typeSB} msgSB={msgSB} />
    </>
  );
}
